import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Loading, Notice, SelectField, TextField } from '../components/ui';
import type { Gruppenauswertung, Warengruppe } from '../types';

interface FormState {
  id: string | null;
  name: string;
  parentId: string;
  sortierung: string;
}

const leer = (parentId = ''): FormState => ({ id: null, name: '', parentId, sortierung: '0' });

export function ProductGroupsPage() {
  const { can } = useAuth();
  const darfPflegen = can('admin', 'kaufmann', 'disponent');

  const [gruppen, setGruppen] = useState<Warengruppe[] | null>(null);
  const [auswertung, setAuswertung] = useState<Gruppenauswertung[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      const [g, a] = await Promise.all([
        api<Warengruppe[]>('/api/articles/groups'),
        api<Gruppenauswertung[]>('/api/articles/groups/auswertung'),
      ]);
      setGruppen(g);
      setAuswertung(a);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function speichern() {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const rumpf = {
        name: form.name,
        parentId: form.parentId || null,
        sortierung: form.sortierung === '' ? 0 : Number(form.sortierung),
      };
      if (form.id) {
        await api(`/api/articles/groups/${form.id}`, { method: 'PUT', body: rumpf });
      } else {
        await api('/api/articles/groups', { method: 'POST', body: rumpf });
      }
      setMessage(form.id ? 'Warengruppe gespeichert.' : 'Warengruppe angelegt.');
      setForm(null);
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function loeschen(g: Warengruppe) {
    if (!confirm(`Warengruppe „${g.name}" löschen?`)) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/articles/groups/${g.id}`, { method: 'DELETE' });
      setMessage('Warengruppe gelöscht.');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!gruppen) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  // Für die Obergruppen-Auswahl: die zu bearbeitende Gruppe und ihre Nachkommen
  // dürfen nicht wählbar sein - das verhinderte der Server ohnehin, aber ein
  // Feld, das gar nicht erst anbietet, was scheitern würde, ist freundlicher.
  const auswahl = gruppen
    .filter((g) => !form?.id || g.id !== form.id)
    .map((g) => ({ id: g.id, label: `${'  '.repeat(g.ebene)}${g.name}` }));

  const werte = new Map(auswertung.map((a) => [a.id, a]));

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {darfPflegen && (
        <div style={{ marginBottom: 12 }}>
          <button className="primary" onClick={() => setForm(leer())} disabled={busy}>
            Neue Warengruppe
          </button>
        </div>
      )}

      {form && (
        <Card title={form.id ? 'Warengruppe bearbeiten' : 'Neue Warengruppe'}>
          <div className="form-grid">
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} autoFocus />
            <SelectField
              label="Obergruppe"
              value={form.parentId}
              onChange={(v) => setForm({ ...form, parentId: v })}
              options={auswahl}
              render={(o) => o.label}
              placeholder="— oberste Ebene —"
            />
            <TextField
              label="Sortierung"
              type="number"
              value={form.sortierung}
              onChange={(v) => setForm({ ...form, sortierung: v })}
              hint="Kleinere Zahl steht oben; gleiche Zahl alphabetisch."
            />
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="primary" onClick={speichern} disabled={busy || !form.name.trim()}>
              Speichern
            </button>
            <button onClick={() => setForm(null)} disabled={busy}>
              Abbrechen
            </button>
          </div>
        </Card>
      )}

      <Card title="Warengruppen" padded={false}>
        {gruppen.length === 0 ? (
          <div className="body">
            <p className="muted small">
              Noch keine Warengruppen. Sie ordnen Artikel fachlich und tragen sich als Baum —
              eine Obergruppe wie „Elektro", darunter „Leuchtmittel", „Kleingeräte".
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Gruppe</th>
                  <th className="num">Artikel</th>
                  <th className="num">Bestand</th>
                  <th className="num">Warenwert (EK)</th>
                  <th className="num">Ø Spanne</th>
                  {darfPflegen && <th />}
                </tr>
              </thead>
              <tbody>
                {gruppen.map((g) => {
                  const w = werte.get(g.id);
                  return (
                    <tr key={g.id}>
                      <td style={{ paddingLeft: 12 + g.ebene * 22 }}>
                        {g.ebene > 0 && <span className="muted">› </span>}
                        {g.name}
                        {!g.active && (
                          <>
                            {' '}
                            <Badge kind="bad">inaktiv</Badge>
                          </>
                        )}
                        {g.kinder > 0 && <span className="small muted"> · {g.kinder} Untergruppen</span>}
                      </td>
                      <td className="num">{g.artikel_direkt || '—'}</td>
                      <td className="num muted">
                        {w && w.bestand_einheiten !== null
                          ? formatQty(w.bestand_einheiten, w.einheit ?? undefined)
                          : w && w.artikel > 0
                            ? 'gemischt'
                            : '—'}
                      </td>
                      <td className="num">{w && w.bestandswert_ek_cents ? formatMoney(w.bestandswert_ek_cents) : '—'}</td>
                      <td className="num">{w && w.spanne_schnitt !== null ? `${w.spanne_schnitt} %` : '—'}</td>
                      {darfPflegen && (
                        <td className="num" style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="small ghost"
                            onClick={() =>
                              setForm({
                                id: g.id,
                                name: g.name,
                                parentId: g.parent_id ?? '',
                                sortierung: String(g.sortierung),
                              })
                            }
                          >
                            bearbeiten
                          </button>{' '}
                          <button className="small ghost" onClick={() => loeschen(g)} disabled={busy}>
                            löschen
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
